import { c as _c } from "react/compiler-runtime";
import type { DashboardSecretApiKey } from '@onefootprint/request-types/dashboard';
import { Badge } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import usePermissions from '@/hooks/use-permissions';
import EditRole from './components/edit-role';
import KeyCell from './components/key-cell';
import RowMenu from './components/row-menu';
import useReveal from './hooks/use-reveal-key';
import useUpdateStatus from './hooks/use-update-status';
type RowProps = {
  apiKey: DashboardSecretApiKey;
};
const Row = t0 => {
  const $0 = _c(35);
  const {
    apiKey
  } = t0;
  let t1;
  if ($0[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "table"
    };
    $0[0] = t1;
  } else {
    t1 = $0[0];
  }
  const {
    t
  } = useTranslation("api-keys", t1);
  const reveal = useReveal(apiKey);
  const status = useUpdateStatus(apiKey);
  const isEnabled = apiKey.status === "enabled";
  const {
    hasPermission
  } = usePermissions();
  let t2;
  if ($0[1] !== apiKey.name) {
    t2 = <td>{apiKey.name}</td>;
    $0[1] = apiKey.name;
    $0[2] = t2;
  } else {
    t2 = $0[2];
  }
  let t3;
  if ($0[3] !== apiKey || $0[4] !== reveal.mutation.isPending) {
    t3 = <td aria-label="cell"><KeyCell isPending={reveal.mutation.isPending} value={apiKey} /></td>;
    $0[3] = apiKey;
    $0[4] = reveal.mutation.isPending;
    $0[5] = t3;
  } else {
    t3 = $0[5];
  }
  const t4 = apiKey.lastUsedAt || "--";
  let t5;
  if ($0[6] !== t4) {
    t5 = <td>{t4}</td>;
    $0[6] = t4;
    $0[7] = t5;
  } else {
    t5 = $0[7];
  }
  let t6;
  if ($0[8] !== apiKey.createdAt) {
    t6 = <td>{apiKey.createdAt}</td>;
    $0[8] = apiKey.createdAt;
    $0[9] = t6;
  } else {
    t6 = $0[9];
  }
  const t7 = isEnabled ? "success" : "error";
  let t8;
  if ($0[10] !== apiKey.status) {
    t8 = $ => $.statuses[apiKey.status];
    $0[10] = apiKey.status;
    $0[11] = t8;
  } else {
    t8 = $0[11];
  }
  let t9;
  if ($0[12] !== t || $0[13] !== t8) {
    t9 = t(t8);
    $0[12] = t;
    $0[13] = t8;
    $0[14] = t9;
  } else {
    t9 = $0[14];
  }
  let t10;
  if ($0[15] !== t7 || $0[16] !== t9) {
    t10 = <td><Badge variant={t7}>{t9}</Badge></td>;
    $0[15] = t7;
    $0[16] = t9;
    $0[17] = t10;
  } else {
    t10 = $0[17];
  }
  let t11;
  if ($0[18] !== apiKey || $0[19] !== hasPermission) {
    t11 = hasPermission("org_settings") ? <EditRole apiKey={apiKey} /> : apiKey.role.name;
    $0[18] = apiKey;
    $0[19] = hasPermission;
    $0[20] = t11;
  } else {
    t11 = $0[20];
  }
  let t12;
  if ($0[21] !== t11) {
    t12 = <td>{t11}</td>;
    $0[21] = t11;
    $0[22] = t12;
  } else {
    t12 = $0[22];
  }
  let t13;
  if ($0[23] !== apiKey || $0[24] !== reveal.toggle || $0[25] !== status.toggle) {
    t13 = <td><div className="flex justify-end"><RowMenu apiKey={apiKey} onToggleReveal={reveal.toggle} onToggleStatus={status.toggle} /></div></td>;
    $0[23] = apiKey;
    $0[24] = reveal.toggle;
    $0[25] = status.toggle;
    $0[26] = t13;
  } else {
    t13 = $0[26];
  }
  let t14;
  if ($0[27] !== t10 || $0[28] !== t12 || $0[29] !== t13 || $0[30] !== t2 || $0[31] !== t3 || $0[32] !== t5 || $0[33] !== t6) {
    t14 = <>{t2}{t3}{t5}{t6}{t10}{t12}{t13}</>;
    $0[27] = t10;
    $0[28] = t12;
    $0[29] = t13;
    $0[30] = t2;
    $0[31] = t3;
    $0[32] = t5;
    $0[33] = t6;
    $0[34] = t14;
  } else {
    t14 = $0[34];
  }
  return t14;
};
const MobileRow = t0 => {
  const $0 = _c(50);
  const {
    apiKey
  } = t0;
  let t1;
  if ($0[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "table"
    };
    $0[0] = t1;
  } else {
    t1 = $0[0];
  }
  const {
    t
  } = useTranslation("api-keys", t1);
  const reveal = useReveal(apiKey);
  const status = useUpdateStatus(apiKey);
  const isEnabled = apiKey.status === "enabled";
  const {
    hasPermission
  } = usePermissions();
  let t2;
  if ($0[1] !== apiKey.name) {
    t2 = <p className="min-w-0 flex-1 truncate text-label-2 text-primary">{apiKey.name}</p>;
    $0[1] = apiKey.name;
    $0[2] = t2;
  } else {
    t2 = $0[2];
  }
  const t3 = isEnabled ? "success" : "error";
  let t4;
  if ($0[3] !== apiKey.status) {
    t4 = $ => $.statuses[apiKey.status];
    $0[3] = apiKey.status;
    $0[4] = t4;
  } else {
    t4 = $0[4];
  }
  let t5;
  if ($0[5] !== t || $0[6] !== t4) {
    t5 = t(t4);
    $0[5] = t;
    $0[6] = t4;
    $0[7] = t5;
  } else {
    t5 = $0[7];
  }
  let t6;
  if ($0[8] !== t3 || $0[9] !== t5) {
    t6 = <Badge className="shrink-0" variant={t3}>{t5}</Badge>;
    $0[8] = t3;
    $0[9] = t5;
    $0[10] = t6;
  } else {
    t6 = $0[10];
  }
  let t7;
  if ($0[11] !== apiKey || $0[12] !== reveal.toggle || $0[13] !== status.toggle) {
    t7 = <RowMenu apiKey={apiKey} onToggleReveal={reveal.toggle} onToggleStatus={status.toggle} size="default" />;
    $0[11] = apiKey;
    $0[12] = reveal.toggle;
    $0[13] = status.toggle;
    $0[14] = t7;
  } else {
    t7 = $0[14];
  }
  let t8;
  if ($0[15] !== t2 || $0[16] !== t6 || $0[17] !== t7) {
    t8 = <div className="flex items-center gap-2">{t2}{t6}{t7}</div>;
    $0[15] = t2;
    $0[16] = t6;
    $0[17] = t7;
    $0[18] = t8;
  } else {
    t8 = $0[18];
  }
  let t9;
  if ($0[19] !== apiKey || $0[20] !== reveal.mutation.isPending) {
    t9 = <div className="flex min-w-0 overflow-hidden"><KeyCell isPending={reveal.mutation.isPending} value={apiKey} /></div>;
    $0[19] = apiKey;
    $0[20] = reveal.mutation.isPending;
    $0[21] = t9;
  } else {
    t9 = $0[21];
  }
  let t10;
  if ($0[22] !== t) {
    t10 = t(_temp);
    $0[22] = t;
    $0[23] = t10;
  } else {
    t10 = $0[23];
  }
  const t11 = apiKey.lastUsedAt || "--";
  let t12;
  if ($0[24] !== t10 || $0[25] !== t11) {
    t12 = <span>{t10} {t11}</span>;
    $0[24] = t10;
    $0[25] = t11;
    $0[26] = t12;
  } else {
    t12 = $0[26];
  }
  let t13;
  if ($0[27] !== t) {
    t13 = t(_temp2);
    $0[27] = t;
    $0[28] = t13;
  } else {
    t13 = $0[28];
  }
  let t14;
  if ($0[29] !== apiKey.createdAt || $0[30] !== t13) {
    t14 = <span>{t13} {apiKey.createdAt}</span>;
    $0[29] = apiKey.createdAt;
    $0[30] = t13;
    $0[31] = t14;
  } else {
    t14 = $0[31];
  }
  let t15;
  if ($0[32] !== t12 || $0[33] !== t14) {
    t15 = <div className="flex flex-wrap gap-x-3 gap-y-1 text-body-3 text-tertiary">{t12}{t14}</div>;
    $0[32] = t12;
    $0[33] = t14;
    $0[34] = t15;
  } else {
    t15 = $0[34];
  }
  let t16;
  if ($0[35] !== t) {
    t16 = t(_temp3);
    $0[35] = t;
    $0[36] = t16;
  } else {
    t16 = $0[36];
  }
  let t17;
  if ($0[37] !== t16) {
    t17 = <span className="shrink-0 text-tertiary">{t16}</span>;
    $0[37] = t16;
    $0[38] = t17;
  } else {
    t17 = $0[38];
  }
  let t18;
  if ($0[39] !== apiKey || $0[40] !== hasPermission) {
    t18 = hasPermission("org_settings") ? <EditRole apiKey={apiKey} /> : <span className="truncate">{apiKey.role.name}</span>;
    $0[39] = apiKey;
    $0[40] = hasPermission;
    $0[41] = t18;
  } else {
    t18 = $0[41];
  }
  let t19;
  if ($0[42] !== t17 || $0[43] !== t18) {
    t19 = <div className="flex min-w-0 items-center gap-2 text-body-3">{t17}{t18}</div>;
    $0[42] = t17;
    $0[43] = t18;
    $0[44] = t19;
  } else {
    t19 = $0[44];
  }
  let t20;
  if ($0[45] !== t15 || $0[46] !== t19 || $0[47] !== t8 || $0[48] !== t9) {
    t20 = <td><div className="flex flex-col gap-2 py-3">{t8}{t9}{t15}{t19}</div></td>;
    $0[45] = t15;
    $0[46] = t19;
    $0[47] = t8;
    $0[48] = t9;
    $0[49] = t20;
  } else {
    t20 = $0[49];
  }
  return t20;
};
export type { RowProps };
export { MobileRow };
export default Row;
function _temp($_0) {
  return $_0.header.lastUsed;
}
function _temp2($_1) {
  return $_1.header.created;
}
function _temp3($_2) {
  return $_2.header.accessControl.title;
}